import type { NavigateFunction } from "react-router-dom";
import { modalViewsConst } from "../store/modalSlice";
import type { ModalState } from "../store/modalSlice";

// Shareable Data & Models links: ?dataset=<id> or ?model=<id> opens that item's modal, &view=<tab> its tab
export type DetailLinkKind = "dataset" | "model";
export interface DetailLink {
  kind: DetailLinkKind;
  id: string;
}

/** The dataset or model the URL links to, or null. */
export const readDetailLink = (params: URLSearchParams): DetailLink | null => {
  const datasetId = params.get("dataset");
  if (datasetId) return { kind: "dataset", id: datasetId };
  const modelId = params.get("model");
  return modelId ? { kind: "model", id: modelId } : null;
};

// A row's popup inside a linked dataset/model: &upload=<id>, &run=<id> (model runs), &processingRun=<id>, &inputs=<runId>
const DETAIL_ITEM_PARAMS = ["upload", "run", "processingRun", "inputs"];

/** Points the params at this item (no view yet; the modal writes its tab), or removes any link when null. */
export const setDetailLink = (params: URLSearchParams, link: DetailLink | null): void => {
  ["dataset", "model", "view", ...DETAIL_ITEM_PARAMS].forEach((key) => params.delete(key));
  if (link) params.set(link.kind, link.id);
};

/** Writes the open tab into &view when the URL links to this item; any other URL is left alone. */
export const writeDetailLinkView = (navigate: NavigateFunction, kind: DetailLinkKind, id: string | undefined, view: string): void =>
  editUrlParams(navigate, (params) => {
    if (id && params.get(kind) === id) params.set("view", view);
  });

/** Edits the live URL's params: read from window.location, not a render's copy, so two edits in one tick both land. */
export const editUrlParams = (navigate: NavigateFunction, edit: (params: URLSearchParams) => void, replace = true): void => {
  const params = new URLSearchParams(window.location.search);
  const before = params.toString();
  edit(params);
  const search = params.toString();
  if (search === before) return;
  // A push right after a close takes the closed entry's place, so "View inputs" from a run popup is one step, not two
  const swap = !replace && pendingBack !== null;
  if (swap) cancelPendingBack();
  const depth = currentLinkDepth();
  navigate(
    { pathname: window.location.pathname, search: search ? `?${search}` : "" },
    { replace: replace || swap, state: { linkDepth: replace || swap ? depth : depth + 1 } }
  );
};

// How many history entries this app pushed for open links above the page's own entry; pasted links have none
const currentLinkDepth = (): number => window.history.state?.usr?.linkDepth ?? 0;

// A close's step back waits a tick, so an open in the same click can take its place instead
let pendingBack: ReturnType<typeof setTimeout> | null = null;
const cancelPendingBack = (): void => {
  if (pendingBack !== null) clearTimeout(pendingBack);
  pendingBack = null;
};

/** Closes a link: the URL drops it at once, then history steps back over the app's pushes for it (one popup, or all for the modal); a pasted link is only dropped. */
export const closeUrlParams = (navigate: NavigateFunction, edit: (params: URLSearchParams) => void, steps: "one" | "all"): void => {
  const depth = currentLinkDepth();
  editUrlParams(navigate, edit);
  const back = steps === "all" ? depth : Math.min(1, depth);
  if (back === 0) return;
  cancelPendingBack();
  const pathname = window.location.pathname;
  pendingBack = setTimeout(() => {
    pendingBack = null;
    // A navigation to another page in the same tick (a sidebar click) must not be stepped back over
    if (window.location.pathname === pathname) navigate(-back);
  }, 0);
};

/** Drops a row popup's params, for a tab switch: the next tab's rows never hold the last tab's ids. */
export const clearDetailItems = (navigate: NavigateFunction): void => editUrlParams(navigate, (params) => DETAIL_ITEM_PARAMS.forEach((key) => params.delete(key)));

/** Whether the global modal is showing the linked item (dataset modal, or either model modal). */
export const modalShowsDetailLink = (
  modal: ModalState,
  link: DetailLink
): boolean => {
  if (!modal.modalOpen) return false;
  if (link.kind === "dataset") return modal.modalView === modalViewsConst.datasetGraph && modal.properties?.dataset?.id === link.id;
  const shown =
    modal.modalView === modalViewsConst.historicalModel
      ? modal.properties?.model
      : modal.modalView === modalViewsConst.modelOutputDetail
        ? modal.properties?.schema
        : null;
  return shown?.id === link.id;
};
